/**
 * BangProof Chat Widget for Shopify
 * Integrates with n8n webhook for customer support
 */
class BangProofChatWidget {
  constructor() {
    this.isOpen = false;
    this.isLoading = false;
    this.sessionId = this.generateSessionId();
    this.messages = this.loadMessages();
    this.webhookUrl = window.BANGPROOF_CHAT_CONFIG?.webhookUrl || '';
    
    this.init();
  }

  init() {
    this.createWidget();
    this.bindEvents();
    this.observeCartDrawer();
    
    // Add initial welcome message if no messages exist
    if (this.messages.length === 0) {
      this.addMessage('bot', "Hello! I'm your Customer Support assistant. How can I help you today?");
    }
  }

  observeCartDrawer() {
    const hideChat = () => {
      if (this.isOpen) this.toggleChat();
      this.widget.style.display = 'none';
    };

    const showChat = () => {
      this.widget.style.display = '';
    };

    // Helper to check current drawer state
    const checkDrawer = () => {
      const drawer = document.querySelector('.drawer');
      if (!drawer) return;
      const isActive = drawer.classList.contains('active');
      if (isActive) hideChat(); else showChat();
    };

    // Initial check after a tick (drawer may render later)
    setTimeout(checkDrawer, 0);

    // Observe body for drawer state changes
    const observer = new MutationObserver(() => {
      checkDrawer();
    });

    observer.observe(document.documentElement || document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });

    // Also listen to custom events Shopify may dispatch
    document.addEventListener('cart-drawer:open', hideChat);
    document.addEventListener('cart-drawer:close', showChat);
  }

  generateSessionId() {
    return (
      Math.random().toString(36).substr(2, 9) +
      Math.random().toString(36).substr(2, 9) +
      Math.random().toString(36).substr(2, 9) +
      Math.random().toString(36).substr(2, 7)
    );
  }

  loadMessages() {
    try {
      const stored = sessionStorage.getItem('bangproof_chat_messages');
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('[BangProof Chat] Error loading messages:', error);
      return [];
    }
  }

  saveMessages() {
    try {
      sessionStorage.setItem('bangproof_chat_messages', JSON.stringify(this.messages));
    } catch (error) {
      console.error('[BangProof Chat] Error saving messages:', error);
    }
  }

  createWidget() {
    // Create widget container
    this.widget = document.createElement('div');
    this.widget.id = 'bangproof-chat-widget';
    this.widget.innerHTML = `
      <!-- Chat Button (collapsed state) -->
      <div class="bangproof-chat-button" id="bangproof-chat-button">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 2H4C2.9 2 2.01 2.9 2.01 4L2 22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2ZM18 14H6V12H18V14ZM18 11H6V9H18V11ZM18 8H6V6H18V8Z" fill="currentColor"/>
        </svg>
        <div class="bangproof-chat-notification" id="bangproof-chat-notification" style="display: none;"></div>
      </div>

      <!-- Chat Window (expanded state) -->
      <div class="bangproof-chat-window" id="bangproof-chat-window" style="display: none;">
        <!-- Header -->
        <div class="bangproof-chat-header">
          <div class="bangproof-chat-header-content">
            <div class="bangproof-chat-avatar">
              <span>🏄‍♀️</span>
            </div>
            <div class="bangproof-chat-header-text">
              <h3>Customer Support</h3>
              <p>We're here to help</p>
            </div>
          </div>
          <button class="bangproof-chat-close" id="bangproof-chat-close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>

        <!-- Messages -->
        <div class="bangproof-chat-messages" id="bangproof-chat-messages">
          <!-- Messages will be populated here -->
        </div>

        <!-- Input -->
        <div class="bangproof-chat-input-container">
          <div class="bangproof-chat-input-wrapper">
            <input 
              type="text" 
              id="bangproof-chat-input" 
              placeholder="Type your message..."
              autocomplete="off"
              maxlength="1000"
            />
            <button class="bangproof-chat-send" id="bangproof-chat-send" disabled>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
          <div class="bangproof-chat-status" id="bangproof-chat-status">
            Press Enter to send
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(this.widget);
  }

  bindEvents() {
    // Chat button click
    document.getElementById('bangproof-chat-button').addEventListener('click', () => {
      this.toggleChat();
    });

    // Close button click
    document.getElementById('bangproof-chat-close').addEventListener('click', () => {
      this.toggleChat();
    });

    // Input events
    const input = document.getElementById('bangproof-chat-input');
    const sendButton = document.getElementById('bangproof-chat-send');

    input.addEventListener('input', (e) => {
      const hasText = e.target.value.trim().length > 0;
      sendButton.disabled = !hasText || this.isLoading;
      sendButton.classList.toggle('bangproof-chat-send-enabled', hasText && !this.isLoading);
    });

    input.addEventListener('keypress', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.sendMessage();
      }
    });

    sendButton.addEventListener('click', () => {
      this.sendMessage();
    });

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.toggleChat();
      }
    });

    // Click outside to close
    document.addEventListener('click', (e) => {
      if (this.isOpen && !this.widget.contains(e.target)) {
        this.toggleChat();
      }
    });
  }

  toggleChat() {
    this.isOpen = !this.isOpen;
    const button = document.getElementById('bangproof-chat-button');
    const window = document.getElementById('bangproof-chat-window');

    if (this.isOpen) {
      button.style.display = 'none';
      window.style.display = 'flex';
      // Hide notification when opening chat
      this.hideNotification();
      this.renderMessages();
      this.scrollToBottom();
      // Focus input after a short delay to ensure it's rendered
      setTimeout(() => {
        document.getElementById('bangproof-chat-input').focus();
      }, 100);
    } else {
      button.style.display = 'flex';
      window.style.display = 'none';
    }
  }

  renderMessages() {
    const container = document.getElementById('bangproof-chat-messages');
    container.innerHTML = '';

    this.messages.forEach(message => {
      const messageEl = this.createMessageElement(message);
      container.appendChild(messageEl);
    });

    this.scrollToBottom();
  }

  createMessageElement(message) {
    const messageEl = document.createElement('div');
    messageEl.className = `bangproof-chat-message bangproof-chat-message-${message.sender}`;
    
    const timestamp = new Date(message.timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });

    if (message.sender === 'bot') {
      messageEl.innerHTML = `
        <div class="bangproof-chat-message-avatar">
          <span>🏄‍♀️</span>
        </div>
        <div class="bangproof-chat-message-content">
          <div class="bangproof-chat-message-bubble">
            ${this.formatMessageContent(message.content)}
          </div>
          <div class="bangproof-chat-message-time">${timestamp}</div>
        </div>
      `;
    } else {
      messageEl.innerHTML = `
        <div class="bangproof-chat-message-content">
          <div class="bangproof-chat-message-bubble">
            ${this.formatMessageContent(message.content)}
          </div>
          <div class="bangproof-chat-message-time">${timestamp}</div>
        </div>
      `;
    }

    return messageEl;
  }

  formatMessageContent(content) {
    // Basic HTML escaping and simple formatting
    return content
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;')
      .replace(/\n/g, '<br>');
  }

  addMessage(sender, content) {
    const message = {
      id: Date.now().toString(),
      content: content,
      sender: sender,
      timestamp: new Date().toISOString()
    };

    this.messages.push(message);
    this.saveMessages();

    if (this.isOpen) {
      const container = document.getElementById('bangproof-chat-messages');
      const messageEl = this.createMessageElement(message);
      container.appendChild(messageEl);
      this.scrollToBottom();
    }

    // Show notification if chat is closed and it's a bot message
    if (!this.isOpen && sender === 'bot') {
      this.showNotification();
    }
  }

  showNotification() {
    const button = document.getElementById('bangproof-chat-button');
    button.classList.add('has-notification');
    console.log('[BangProof Chat] Notification shown - button should pulse blue');
  }

  hideNotification() {
    const button = document.getElementById('bangproof-chat-button');
    button.classList.remove('has-notification');
    console.log('[BangProof Chat] Notification hidden - button back to normal');
  }

  // Test function - remove after testing
  testNotification() {
    console.log('[BangProof Chat] Testing blue pulse notification...');
    this.showNotification();
    // Auto-hide after 5 seconds for testing
    setTimeout(() => {
      this.hideNotification();
    }, 5000);
  }

  async sendMessage() {
    const input = document.getElementById('bangproof-chat-input');
    const message = input.value.trim();
    
    if (!message || this.isLoading) return;

    // Add user message
    this.addMessage('user', message);
    input.value = '';
    
    // Update UI for loading state
    this.setLoading(true);

    try {
      const response = await this.callWebhook(message);
      this.addMessage('bot', response);
    } catch (error) {
      console.error('[BangProof Chat] Error:', error);
      this.addMessage('bot', '❌ Sorry, something went wrong. Please try again or contact us directly if the issue persists.');
    } finally {
      this.setLoading(false);
    }
  }

  async callWebhook(message) {
    if (!this.webhookUrl) {
      throw new Error('Webhook URL not configured');
    }

    const payload = {
      sessionId: this.sessionId,
      action: 'sendMessage',
      chatInput: message
    };

    console.log('[BangProof Chat] Sending message to webhook:', payload);

    const response = await fetch(this.webhookUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status} ${response.statusText}`);
    }

    const contentType = response.headers.get('content-type');
    let data;

    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
      
      // Handle different response formats from n8n
      let botResponse;
      if (Array.isArray(data) && data.length > 0) {
        const responseItem = data[0];
        botResponse = responseItem.output || 
                     responseItem.chatInput || 
                     responseItem.response || 
                     responseItem.message || 
                     'Message processed successfully!';
      } else if (typeof data === 'object' && data !== null) {
        botResponse = data.output || 
                     data.chatInput || 
                     data.response || 
                     data.message || 
                     data.reply || 
                     data.text || 
                     'Message processed successfully!';
      } else {
        botResponse = 'Message processed successfully!';
      }
      
      return botResponse;
    } else {
      const textResponse = await response.text();
      return textResponse || 'Message received successfully!';
    }
  }

  setLoading(loading) {
    this.isLoading = loading;
    const input = document.getElementById('bangproof-chat-input');
    const sendButton = document.getElementById('bangproof-chat-send');
    const status = document.getElementById('bangproof-chat-status');

    input.disabled = loading;
    sendButton.disabled = loading || !input.value.trim();
    sendButton.classList.toggle('bangproof-chat-send-enabled', !loading && input.value.trim());

    if (loading) {
      status.textContent = 'Sending...';
      this.showTypingIndicator();
    } else {
      status.textContent = 'Press Enter to send';
      this.hideTypingIndicator();
    }
  }

  showTypingIndicator() {
    if (this.isOpen) {
      const container = document.getElementById('bangproof-chat-messages');
      
      // Remove existing typing indicator
      const existingTyping = container.querySelector('.bangproof-chat-typing');
      if (existingTyping) {
        existingTyping.remove();
      }

      const typingEl = document.createElement('div');
      typingEl.className = 'bangproof-chat-message bangproof-chat-message-bot bangproof-chat-typing';
      typingEl.innerHTML = `
        <div class="bangproof-chat-message-avatar">
          <span>🏄‍♀️</span>
        </div>
        <div class="bangproof-chat-message-content">
          <div class="bangproof-chat-message-bubble">
            <div class="bangproof-chat-typing-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      `;
      
      container.appendChild(typingEl);
      this.scrollToBottom();
    }
  }

  hideTypingIndicator() {
    const typingEl = document.querySelector('.bangproof-chat-typing');
    if (typingEl) {
      typingEl.remove();
    }
  }

  scrollToBottom() {
    const container = document.getElementById('bangproof-chat-messages');
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }
}

// Initialize widget when DOM is loaded
document.addEventListener('DOMContentLoaded', function() {
  // Check if widget should be enabled
  if (typeof window.BANGPROOF_CHAT_CONFIG !== 'undefined' && window.BANGPROOF_CHAT_CONFIG.enabled) {
    window.bangproofChat = new BangProofChatWidget();
    
    // Global test function for debugging
    window.testChatNotification = function() {
      console.log('[BangProof Chat] Testing notification manually...');
      if (window.bangproofChat) {
        window.bangproofChat.testNotification();
      }
    };
  }
});

